import React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import IconButton from "@mui/joy/IconButton";
import CircularProgress from "@mui/joy/CircularProgress";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import {NotificationService} from "../../common/notification";
import EditorDialog from "./EditorDialog";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {bannersListApi, addBannersApi, updateBannersApi, deleteBannersApi} from "./bannersApi";
import {Banner} from "../../models/banner";
import Avatar from "@mui/joy/Avatar";
import {RequestResult} from "../../common/api/requestResult";
import ConfirmDialog from "../../components/confirmDialog/ConfirmDialog";

const emptyBanner = {
    id: 0,
    canClose: false,
    cellCount: 2,
    gameIconUrl: '',
    gameId: 0,
    gameName: '',
    isActive: false,
    urlRu: '',
    urlEn: '',
    urlEnMobile:'',
    urlRuMobile:'',
    redirectUrl: ''
};

export function BannersList() {
    const [banners, setBanner] = React.useState<Banner[]>([]);
    const [editorOpen, setEditorOpen] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    const [selectedBanner, setSelectedBanner] = React.useState<Banner>(emptyBanner);

    const [loading, setLoading] = React.useState(false);
    const id = React.useRef(0);

    React.useEffect(() => {
        loadData().catch(console.error);
    }, [])

    const loadData = async () => {
        setLoading(true)
        try {
            let result = await bannersListApi();
            if (result.result) {
                setBanner(result.value!)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false)
        }
    }

    const edit = (banner: Banner) => {
        setSelectedBanner(banner);
        setEditorOpen(true);
    }

    const onSave = async (banner: Banner, fileRu?: File, fileEn?: File, fileMobileRu?: File, fileMobileEn?: File): Promise<boolean> => {
        if (banner.id === 0) {
            if (!fileRu || !fileEn) {
                NotificationService.alertError("Пожалуйста задайте обе картинки")
                return false;
            }
        }
        setLoading(true)
        try {
            let res: RequestResult;
            if (banner.id === 0) {
                res = await addBannersApi(banner.gameId, banner.cellCount, banner.canClose, banner.isActive, fileRu!, fileEn!, fileMobileRu!, fileMobileEn!, banner.redirectUrl);
            } else {
                res = await updateBannersApi(banner.id, banner.gameId, banner.cellCount, banner.canClose, banner.isActive, fileRu, fileEn, fileMobileRu, fileMobileEn, banner.redirectUrl);
            }

            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
                return false;
            }
            await loadData()
            return true;
        } finally {
            setLoading(false)
        }
    }

    const remove = (value: number) => {
        id.current = value;
        setConfirmOpen(true)
    }

    const onDelete = async () => {
        setLoading(true)
        try {
            const res = await deleteBannersApi(id.current);
            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
                return;
            }
            await loadData()
        } finally {
            id.current = 0;
            setLoading(false)
        }
    }

    return (
        <Box sx={{display: 'flex', minHeight: '100dvh'}}>
            <ConfirmDialog
                title="Удалить?"
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={onDelete}
            >
                Вы уверены что хотите удалить банер?
            </ConfirmDialog>
            {editorOpen &&
                <EditorDialog banner={selectedBanner}
                              onConfirm={onSave}
                              open={editorOpen}
                              setOpen={setEditorOpen}/>
            }

            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: {xs: 2, md: 6},
                    pt: {
                        xs: 'calc(12px + var(--Header-height))',
                        sm: 'calc(12px + var(--Header-height))',
                        md: 3,
                    },
                    pb: {xs: 2, sm: 2, md: 3},
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    height: '100dvh',
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        mb: 1,
                        gap: 1,
                        flexDirection: {xs: 'column', sm: 'row'},
                        alignItems: {xs: 'start', sm: 'center'},
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography level="h2" component="h1">
                        Банеры
                    </Typography>
                </Box>
                <React.Fragment>

                    <Box
                        className="add-tabletUp"
                        sx={{
                            borderRadius: 'sm',
                            py: 2,
                            display: {xs: 'none', sm: 'flex'},
                            flexWrap: 'wrap',
                            gap: 1.5,
                            '& > *': {
                                minWidth: {xs: '120px', md: '160px'},
                            },
                        }}
                    >
                        <IconButton
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            onClick={() => edit(emptyBanner)}
                        >
                            <AddIcon/>
                        </IconButton>
                    </Box>
                    {loading ?
                        <CircularProgress/> :
                        <>
                            <Sheet
                                className="OrderTableContainer"
                                variant="outlined"
                                sx={{
                                    display: {xs: 'none', sm: 'initial'},
                                    width: '100%',
                                    borderRadius: 'sm',
                                    flexShrink: 1,
                                    overflow: 'auto',
                                    minHeight: 0,
                                }}
                            >
                                <Table
                                    aria-labelledby="tableTitle"
                                    stickyHeader
                                    hoverRow
                                    sx={{
                                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                        '--Table-headerUnderlineThickness': '1px',
                                        '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                        '--TableCell-paddingY': '4px',
                                        '--TableCell-paddingX': '8px',
                                    }}
                                >
                                    <thead>
                                    <tr>
                                        <th style={{width: 140, padding: '12px 6px'}}>Игра</th>
                                        <th style={{width: 100, padding: '12px 6px'}}>Ширина</th>
                                        <th style={{width: 100, padding: '12px 6px'}}>Можно закрыть</th>
                                        <th style={{width: 100, padding: '12px 6px'}}>Активный</th>
                                        <th style={{width: 240, padding: '12px 6px'}}>Ссылка</th>
                                        <th style={{width: 140, padding: '12px 6px'}}></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {banners.sort((a, b) => {
                                        if (a.isActive === b.isActive) {
                                            return 0;
                                        }
                                        return a.isActive ? -1 : 1;
                                    }).map((row) => (
                                        <tr key={row.id} style={row.isActive ? {background: "#72e57c"} : {}}>
                                            <td>
                                                <Stack spacing={1} direction={'row'} alignItems={'center'}>
                                                    <Avatar
                                                        variant="outlined"
                                                        size="sm"
                                                        src={row.gameIconUrl}
                                                    />
                                                    <Typography level="body-xs">{row.gameName}</Typography>
                                                </Stack>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">{row.cellCount}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">{row.canClose ? "Да" : "Нет"}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">{row.isActive ? "Да" : "Нет"}</Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-xs">{row.redirectUrl}</Typography>
                                            </td>
                                            <td>
                                                <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                                                    <IconButton
                                                        size="sm"
                                                        variant="outlined"
                                                        color="neutral"
                                                        onClick={() => edit(row)}
                                                    >
                                                        <EditIcon/>
                                                    </IconButton>
                                                    <IconButton
                                                        size="sm"
                                                        variant="outlined"
                                                        color="neutral"
                                                        onClick={() => remove(row.id)}
                                                    >
                                                        <DeleteIcon/>
                                                    </IconButton>
                                                </Box>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            </Sheet>

                            <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    color="neutral"
                                    onClick={() => edit(emptyBanner)}
                                >
                                    <AddIcon/>
                                </IconButton>
                                {banners.sort((a, b) => {
                                    if (a.isActive === b.isActive) {
                                        return 0;
                                    }
                                    return a.isActive ? -1 : 1;
                                }).map((listItem) => (
                                    <List key={listItem.id} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                        <ListItem
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'start',
                                            }}
                                        >
                                            <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                                <Stack spacing={1} direction={'row'}>
                                                    <Avatar
                                                        style={listItem.isActive?{borderColor:'green', borderWidth:3}:{}}
                                                        variant="outlined"
                                                        size="sm"
                                                        src={listItem.gameIconUrl}
                                                    />

                                                    <div>
                                                        <Typography gutterBottom sx={{fontWeight: 600}}>
                                                            {listItem.gameName}
                                                        </Typography>
                                                        <Typography
                                                            level="body-xs">Ширина: {listItem.cellCount}</Typography>
                                                        <Typography level="body-xs">Можно
                                                            закрыть: {listItem.canClose?'Да':'Нет'}</Typography>
                                                        <Typography
                                                            level="body-xs">Активный: {listItem.isActive?'Да':'Нет'}</Typography>
                                                        <Typography
                                                            level="body-xs">Ссылка: {listItem.redirectUrl}</Typography>
                                                        <Box
                                                            sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                            <Link level="body-sm" component="button"
                                                                  onClick={() => edit(listItem)}>
                                                                Редактировать
                                                            </Link>
                                                            <Link level="body-sm" component="button"
                                                                  onClick={() => remove(listItem.id)}>
                                                                Удалить
                                                            </Link>
                                                        </Box>
                                                    </div>
                                                </Stack>
                                            </ListItemContent>
                                        </ListItem>
                                        <ListDivider/>
                                    </List>
                                ))}
                            </Box>
                        </>
                    }
                </React.Fragment>
            </Box>
        </Box>
    );
}
